import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Check, ChevronDown, ChevronRight, Mail, Pencil, Phone, StickyNote, X } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ConnectButton } from "@/features/zdalny-dostep/connect-button";
import { DeviceOperatorNote } from "@/features/zdalny-dostep/device-operator-note";
import { DeviceTile } from "@/features/zdalny-dostep/device-tile";
import { effectiveDeviceStatus } from "@/features/zdalny-dostep/devices-helpers";
import { useDeviceDetailQuery } from "@/features/zdalny-dostep/use-devices-query";
import { badgeBase, statusClasses } from "@/lib/colors";
import { cn } from "@/lib/utils";
import {
	type Contact,
	chatSearch,
	formatDate,
	formatDateTime,
	helpdeskApi,
	type PharmacyPanel as PanelData,
	type PanelDevice,
	SUGGESTION_REASON_LABEL,
	useHelpdeskKey,
	useHelpdeskMutation,
} from "./api";
import { DeviceMessageButton } from "./device-message";
import { PharmacyPrinters } from "./pharmacy-printers";

function Section({
	title,
	count,
	children,
}: {
	title: string;
	count?: number;
	children: React.ReactNode;
}) {
	return (
		<section className="space-y-1.5 border-t border-border px-3 pt-3">
			<h3 className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
				{title}
				{count ? <span className="tabular-nums">{count}</span> : null}
			</h3>
			{children}
		</section>
	);
}

/** Kontakt z podglądem (imię, e-mail, telefon) i edycją w miejscu — w panelu apteki i biurowym. */
export function ContactRow({ contact }: { contact: Pick<Contact, "email" | "name" | "phone"> }) {
	const [editing, setEditing] = useState(false);
	const [name, setName] = useState(contact.name ?? "");
	const [phone, setPhone] = useState(contact.phone ?? "");
	const save = useHelpdeskMutation(() => helpdeskApi.saveContact(contact.email, { name, phone }), {
		success: "Kontakt zapisany",
	});
	if (editing) {
		return (
			<li className="space-y-1 rounded-md border border-border p-1.5">
				<p className="truncate text-xs text-muted-foreground">{contact.email}</p>
				<Input
					value={name}
					onChange={(e) => setName(e.target.value)}
					placeholder="Imię i nazwisko"
					aria-label="Imię i nazwisko"
					className="h-7 text-xs"
				/>
				<Input
					value={phone}
					onChange={(e) => setPhone(e.target.value)}
					placeholder="Telefon"
					aria-label="Telefon"
					className="h-7 text-xs"
				/>
				<div className="flex justify-end gap-1">
					<Button
						size="icon"
						variant="ghost"
						className="size-7"
						aria-label="Anuluj"
						onClick={() => setEditing(false)}
					>
						<X className="size-3.5" />
					</Button>
					<Button
						size="icon"
						className="size-7"
						aria-label="Zapisz kontakt"
						disabled={save.isPending}
						onClick={() => save.mutate(undefined, { onSuccess: () => setEditing(false) })}
					>
						<Check className="size-3.5" />
					</Button>
				</div>
			</li>
		);
	}
	return (
		<li className="group flex items-start gap-2 text-xs">
			<div className="min-w-0 flex-1">
				<p className="truncate font-medium">{contact.name || contact.email}</p>
				{contact.name ? <p className="truncate text-muted-foreground">{contact.email}</p> : null}
				{contact.phone ? (
					<a
						href={`tel:${contact.phone.replace(/\s/g, "")}`}
						className="text-primary hover:underline"
					>
						{contact.phone}
					</a>
				) : null}
			</div>
			<Button
				size="icon"
				variant="ghost"
				className="size-6 opacity-60 group-hover:opacity-100"
				aria-label={`Edytuj ${contact.email}`}
				onClick={() => setEditing(true)}
			>
				<Pencil className="size-3" />
			</Button>
		</li>
	);
}

/**
 * Kontakt z OTRS, który wskazuje tę aptekę, ale nikt go jeszcze nie sparował. Nic nie paruje się
 * samo: „Potwierdź” paruje jak ekran parowania, „To nie ta apteka” zdejmuje tylko tę podpowiedź.
 */
function SuggestedContactRow({
	contact,
	idfirm,
	pharmacyName,
}: {
	contact: PanelData["suggestedContacts"][number];
	idfirm: number;
	pharmacyName: string;
}) {
	const confirm = useHelpdeskMutation(() => helpdeskApi.pair(contact.email, [idfirm]), {
		success: `${contact.email} → ${pharmacyName}`,
		error: "Nie udało się sparować adresu",
	});
	const reject = useHelpdeskMutation(() => helpdeskApi.removeHint(contact.email, idfirm), {
		success: `Podpowiedź usunięta: ${contact.email}`,
		error: "Nie udało się usunąć podpowiedzi",
	});
	const busy = confirm.isPending || reject.isPending;
	return (
		<li className="space-y-1 rounded-md border border-dashed border-border p-1.5 text-xs text-muted-foreground">
			<p className="flex items-center gap-1.5">
				<span className="min-w-0 flex-1 truncate">{contact.name || contact.email}</span>
				<span className={cn(badgeBase, "shrink-0 px-1 py-0")} title={contact.detail ?? undefined}>
					{SUGGESTION_REASON_LABEL[contact.reason] ?? contact.reason}
				</span>
			</p>
			{contact.name ? <p className="truncate">{contact.email}</p> : null}
			{contact.phone ? (
				<a href={`tel:${contact.phone.replace(/\s/g, "")}`} className="hover:underline">
					{contact.phone}
				</a>
			) : null}
			<div className="flex flex-wrap gap-1.5 pt-0.5">
				<Button
					size="sm"
					variant="outline"
					className="h-6 px-2 text-xs"
					disabled={busy}
					onClick={() => confirm.mutate(undefined)}
				>
					Potwierdź
				</Button>
				<Button
					size="sm"
					variant="ghost"
					className="h-6 px-2 text-xs"
					disabled={busy}
					onClick={() => reject.mutate(undefined)}
				>
					To nie ta apteka
				</Button>
			</div>
		</li>
	);
}

/**
 * Stanowisko: nazwa, stan z huba i „Połącz” (ten sam przycisk co w „Urządzeniach”),
 * pod nazwą notatka operatora. Klik w wiersz pokazuje kafel od razu rozwinięty
 * (diagnostyka); „Polecenia” otwiera ten sam dialog co w kaflu.
 */
export function DeviceRow({
	device,
	reported = false,
}: {
	device: PanelDevice;
	reported?: boolean;
}) {
	const [open, setOpen] = useState(false);
	// Każde „Polecenia” montuje kafel na nowo z otwartym dialogiem.
	const [commands, setCommands] = useState(0);
	const [noteFull, setNoteFull] = useState(false);
	const [editingNote, setEditingNote] = useState(false);
	const detail = useDeviceDetailQuery(device.uuid, true);
	// Tylko obecność z huba (szczegóły) — kolumna `status` z panelu bywa nieaktualna.
	const status = detail.data ? effectiveDeviceStatus(detail.data) : detail.isError ? "error" : null;
	const online = status === "online";
	const note = detail.data?.operatorNote?.trim();
	// shortcut: „więcej” po długości, nie po zmierzonym przepełnieniu 3 linii.
	const longNote = !!note && (note.length > 120 || note.split("\n").length > 3);
	const name = device.displayName ?? device.hostname ?? device.uuid;
	return (
		<li
			className={cn(
				"rounded-md border",
				reported ? "border-primary ring-1 ring-primary" : "border-border",
			)}
		>
			<div className="flex h-9 items-center gap-1.5 px-1 text-xs">
				<button
					type="button"
					className="flex h-7 min-w-0 flex-1 items-center gap-1.5 rounded px-1 text-left hover:bg-muted disabled:opacity-60"
					aria-expanded={open}
					aria-label={open ? `Zwiń ${name}` : `Diagnostyka: ${name}`}
					title={device.hostname ?? undefined}
					disabled={!detail.data}
					onClick={() => {
						setOpen((v) => !v);
						// Zwykłe kliknięcie to sama diagnostyka, bez wcześniejszego „Polecenia”.
						setCommands(0);
					}}
				>
					{open ? (
						<ChevronDown className="size-3.5 shrink-0" />
					) : (
						<ChevronRight className="size-3.5 shrink-0" />
					)}
					<span
						className={cn(
							"size-2 shrink-0 rounded-full",
							status === null
								? "bg-muted-foreground/40"
								: online
									? "bg-status-success-text"
									: "bg-status-warning-text",
						)}
						title={status ?? "sprawdzanie"}
					/>
					<span className="sr-only">{status ?? "sprawdzanie"}</span>
					<span className="min-w-0 flex-1 truncate">{name}</span>
					{reported ? <span className="shrink-0 font-medium text-primary">zgłosiło</span> : null}
				</button>
				{status && !online && device.lastSeenAt ? (
					<span className="shrink-0 text-muted-foreground" title="Ostatni kontakt">
						{formatDateTime(device.lastSeenAt)}
					</span>
				) : null}
				{online ? (
					<Button
						size="sm"
						variant="outline"
						className="h-7 shrink-0 px-2 text-xs"
						aria-label={`Polecenia: ${name}`}
						onClick={() => {
							setOpen(true);
							setCommands((n) => n + 1);
						}}
					>
						Polecenia
					</Button>
				) : null}
				{detail.data?.idfirm ? (
					<DeviceMessageButton deviceUuid={device.uuid} idfirm={detail.data.idfirm} name={name} />
				) : null}
				{detail.data ? (
					<ConnectButton device={detail.data} className="h-7 shrink-0 px-2 text-xs" />
				) : null}
			</div>
			{/* Rozwinięty kafel pokazuje notatkę sam. */}
			{editingNote && !open ? (
				<div className="mx-1 mb-1">
					<DeviceOperatorNote
						deviceUuid={device.uuid}
						operatorNote={note ?? null}
						autoEdit
						onDone={() => setEditingNote(false)}
					/>
				</div>
			) : note && !open ? (
				<div
					className={cn(
						"mx-1 mb-1 flex items-start gap-1 rounded border px-1.5 py-1 text-xs",
						statusClasses.warning,
					)}
				>
					<StickyNote className="mt-0.5 size-3 shrink-0" aria-hidden />
					<p
						className={cn(
							"min-w-0 flex-1 whitespace-pre-wrap break-words",
							!noteFull && "line-clamp-3",
						)}
					>
						<span className="sr-only">Notatka operatora: </span>
						{note}
					</p>
					{longNote ? (
						<button
							type="button"
							className="shrink-0 underline"
							onClick={() => setNoteFull((v) => !v)}
						>
							{noteFull ? "mniej" : "więcej"}
						</button>
					) : null}
					<button
						type="button"
						className="shrink-0 rounded p-0.5 opacity-70 hover:opacity-100"
						aria-label={`Edytuj notatkę: ${name}`}
						title="Popraw notatkę"
						onClick={() => setEditingNote(true)}
					>
						<Pencil className="size-3" />
					</button>
				</div>
			) : null}
			{open && detail.data ? (
				<div className="border-t border-border p-1.5">
					<DeviceTile
						key={commands}
						device={detail.data}
						defaultOpen
						defaultCommandsOpen={commands > 0}
					/>
				</div>
			) : null}
		</li>
	);
}

export function PharmacyPanel({
	idfirm,
	currentTicket,
	reportDevice,
}: {
	idfirm: number;
	currentTicket?: number;
	/** Stanowisko, z którego przyszło bieżące zgłoszenie („Zgłoś problem”). */
	reportDevice?: string | null;
}) {
	const key = useHelpdeskKey();
	const { data, isLoading, isError } = useQuery({
		queryKey: key("panel", idfirm),
		queryFn: () => helpdeskApi.panel(idfirm),
		staleTime: 60_000,
	});
	if (isLoading) return <p className="p-3 text-xs text-muted-foreground">Wczytywanie apteki…</p>;
	if (isError || !data)
		return (
			<p className="p-3 text-xs text-status-error-text">Nie udało się pobrać danych apteki.</p>
		);
	const others = data.openTickets.filter((t) => t.number !== currentTicket);
	const address = [
		data.pharmacy.street,
		[data.pharmacy.zip, data.pharmacy.city].filter(Boolean).join(" "),
	]
		.filter(Boolean)
		.join(", ");
	return (
		<div className="space-y-3 pb-3">
			<div className="space-y-1 px-3 pt-3">
				<p className="text-sm font-semibold leading-snug">{data.pharmacy.name}</p>
				{address ? <p className="text-xs text-muted-foreground">{address}</p> : null}
				<p className="flex items-center gap-2 whitespace-nowrap text-xs text-muted-foreground">
					<span className="font-mono">{data.pharmacy.idfirm}</span>
					<span>{data.pharmacy.wlasna ? "własna" : "franczyza"}</span>
					{data.pharmacy.region ? <span className="truncate">{data.pharmacy.region}</span> : null}
					{data.pharmacy.active ? null : (
						<span className={cn(badgeBase, statusClasses.warning)}>nieaktywna</span>
					)}
				</p>
				<div className="flex items-center gap-3 whitespace-nowrap pt-1 text-xs">
					{data.pharmacy.phone ? (
						<a
							href={`tel:${data.pharmacy.phone.replace(/\s/g, "")}`}
							className="inline-flex items-center gap-1 text-primary hover:underline"
						>
							<Phone className="size-3.5" />
							{data.pharmacy.phone}
						</a>
					) : null}
					{data.pharmacy.email ? (
						<a
							href={`mailto:${data.pharmacy.email}`}
							className="inline-flex min-w-0 items-center gap-1 text-primary hover:underline"
							title={data.pharmacy.email}
						>
							<Mail className="size-3.5 shrink-0" />
							<span className="truncate">{data.pharmacy.email}</span>
						</a>
					) : null}
				</div>
				<p className="text-xs text-muted-foreground">
					{data.counts.open} otwarte, {data.counts.closed} zakończone
				</p>
			</div>
			<Section title="Kontakty" count={data.contacts.length}>
				{data.contacts.length ? (
					<ul className="space-y-1.5">
						{data.contacts.map((c) => (
							<ContactRow key={c.email} contact={c} />
						))}
					</ul>
				) : (
					<p className="text-xs text-muted-foreground">Brak sparowanych adresów.</p>
				)}
			</Section>
			{data.suggestedContacts.length ? (
				<Section title="Z OTRS — do potwierdzenia" count={data.suggestedContacts.length}>
					<ul className="space-y-1.5">
						{data.suggestedContacts.map((c) => (
							<SuggestedContactRow
								key={c.email}
								contact={c}
								idfirm={idfirm}
								pharmacyName={data.pharmacy.name}
							/>
						))}
					</ul>
				</Section>
			) : null}
			<Section title="Stanowiska" count={data.devices.length}>
				{data.devices.length ? (
					<ul className="space-y-1.5">
						{data.devices.map((d) => (
							<DeviceRow key={d.uuid} device={d} reported={d.uuid === reportDevice} />
						))}
					</ul>
				) : (
					<p className="text-xs text-muted-foreground">Brak stanowisk z agentem.</p>
				)}
			</Section>
			<PharmacyPrinters idfirm={idfirm} />
			<Section title="Inne otwarte zgłoszenia" count={others.length}>
				{others.length ? (
					<ul className="-mx-1.5">
						{others.map((t) => (
							<li key={t.number}>
								<Link
									to="/helpdesk/czat"
									search={chatSearch({ idfirm, ticket: t.number })}
									title={`${t.subject} — ostatnio ${formatDateTime(t.lastActivityAt)}`}
									className="flex h-7 items-center gap-2 whitespace-nowrap rounded-md px-1.5 text-xs hover:bg-muted"
								>
									<span className="shrink-0 font-mono text-muted-foreground">#{t.number}</span>
									<span className="min-w-0 flex-1 truncate">{t.subject}</span>
									<span className="shrink-0 text-muted-foreground tabular-nums">
										{formatDate(t.lastActivityAt)}
									</span>
								</Link>
							</li>
						))}
					</ul>
				) : (
					<p className="text-xs text-muted-foreground">Brak innych otwartych.</p>
				)}
			</Section>
		</div>
	);
}
